import React, { useState, useEffect, useRef, useMemo } from 'react';
import { formatBrandMoney, formatBrandMoneyCompact, getBrandLocale, getBrandVatRate } from '../../shared/utils/brandCurrency';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import { DollarSign, Search, Plus, Edit, Trash2, RefreshCcw, Filter, Eye, X, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, LayoutGrid, Target, Calendar, Clock, List, LayoutList, Check, Percent, Users, TrendingUp, FileText, CheckCircle, User, Layers, Receipt } from 'lucide-react';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import useColumnHeaderFilters from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import localApiAdapter from '../../core/services/localApiAdapter';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import { generateReportExcel } from '../../shared/utils/reportExcelGeneratorAdvanced';
import { CustomSelect, CustomDatePicker, CustomNumberInput } from '../../shared/components/custom';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { makePageT } from '../../shared/utils/pageI18n';
import VentesListPageChrome, { facturationTableScrollClass } from './components/VentesListPageChrome';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import {
  mapVentesStatCards,
  useCrudModalBodyLock,
  initVentesColumnVisibility,
  buildVentesQawaemColumnDefs,
  isVentesColumnVisible,
  buildVentesBulkDeleteFilterAction,
  computeVentesRowSelection,
  resolveVentesEntityReference,
  useVentesEntityReference,
  VENTES_REF_INPUT_CLASS,
  VentesConfigNotice,
  sliceVentesListItems,
  matchesVentesListDateRange,
} from './ventesListPageUtils';
import { useVentesConfig } from './useVentesConfig';
import { useSettings } from '../../core/contexts/SettingsContext';
import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
import QawaemErpCrudModal, { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';

const CommissionsPage = () => {
  const { t, i18n } = useTranslation();
  const p = makePageT(t, 'modules.ventes.pages.commissionsPage');
  const { isDashboardLightMode } = useGlobalTheme();
  const qawaem = isSaudiBrand();
  const dateLocale = getBrandLocale(i18n.language);
  const vatRate = getBrandVatRate();

  const [searchTerm, setSearchTerm] = useState('');
  const [commissions, setCommissions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [viewMode, setViewMode] = useState('table');
  const ventesConfig = useVentesConfig();
  const { dateRange } = useSaudiListDateRange();

  useEffect(() => {
    setViewMode(ventesConfig.defaultView || 'table');
  }, [ventesConfig.defaultView]);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);

  useCrudModalBodyLock(showModal, showViewModal);
  const { settings } = useSettings();
  const generateCommissionRef = useVentesEntityReference('commission', commissions);
  
  const [formTab, setFormTab] = useState('basic');
  
  const [formData, setFormData] = useState({ 
    commission_reference: '',
    salesperson_name: '', 
    employee_id: '',
    sale_reference: '',
    sale_amount: '',
    commission_rate: '',
    commission_amount: '',
    commission_date: '',
    payment_date: '',
    status: 'pending',
    payment_method: '',
    notes: ''
  });
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_commissions');
  const [quickFilter, setQuickFilter] = useState('all');
  
  // PDF and Excel export states
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  
  const commissionFormTabs = useMemo(() => ([
    { id: 'basic', label: p('section_basic'), icon: User, iconColor: 'text-blue-400' },
    { id: 'commission', label: p('section_commission_details'), icon: DollarSign, iconColor: 'text-green-400' },
  ]), [p]);

  const { filters, setFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const thFilterClass = 'text-white font-semibold py-4';

  const tableColumnDefs = useMemo(
    () => [
      { key: 'commission_reference', label: p('col_code'), required: true },
      { key: 'salesperson_name', label: p('col_salesperson') },
      { key: 'sale_reference', label: p('col_sale_ref') },
      { key: 'commission_rate', label: p('col_rate') },
      { key: 'commission_amount', label: p('col_commission') },
      { key: 'commission_date', label: p('col_date') },
      { key: 'status', label: p('col_status') },
    ],
    [p],
  );
  const [columnVisibility, setColumnVisibility] = useState(() =>
    initVentesColumnVisibility(tableColumnDefs.map((c) => c.key)),
  );
  const qawaemColumnDefs = useMemo(
    () => buildVentesQawaemColumnDefs(tableColumnDefs, columnVisibility),
    [tableColumnDefs, columnVisibility],
  );
  const handleToggleColumn = (key) => {
    setColumnVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;
  const showCol = (key) => isVentesColumnVisible(columnVisibility, key);
  const tableColSpan = tableColumnDefs.filter((c) => showCol(c.key)).length + 2;

  useEffect(() => { loadCommissions(); }, []);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadCommissions = async () => {
    try {
      const response = await localApiAdapter.get('/ventes/commissions');
      const rows = response?.data || response || [];
      setCommissions(sortRecordsNewestFirst(Array.isArray(rows) ? rows : []));
    } catch (error) {
      console.error('Error loading commissions:', error);
      setCommissions([]);
    }
  };

  const stats = {
    total: commissions.length,
    pending: commissions.filter(c => c.status === 'pending').length,
    approved: commissions.filter(c => c.status === 'approved').length,
    paid: commissions.filter(c => c.status === 'paid').length,
    totalCommissionAmount: commissions.reduce((sum, c) => sum + (parseFloat(c.commission_amount) || 0), 0),
    totalPaid: commissions.filter(c => c.status === 'paid').reduce((sum, c) => sum + (parseFloat(c.commission_amount) || 0), 0)
  };

  const mainTabs = useMemo(() => ([
    { id: 'all_commissions', name: t('common.general_view'), icon: LayoutGrid },
    { id: 'by_salesperson', name: p('tab_by_salesperson'), icon: Users },
    { id: 'by_status', name: t('common.by_status'), icon: CheckCircle },
  ]), [t, p]);

  const quickFilterButtons = useMemo(() => ([
    { id: 'all', name: t('common.all'), icon: Layers },
    { id: 'pending', name: p('filter_pending'), icon: Clock },
    { id: 'approved', name: p('filter_approved'), icon: CheckCircle },
    { id: 'paid', name: p('filter_paid'), icon: Check },
  ]), [t, p]);

  const commissionDistribution = useMemo(() => ({
    all_commissions: {
      title: p('dist_overview_title'),
      data: [
        { filterKey: 'pending', name: p('dist_pending'), count: stats.pending, color: 'bg-gradient-to-r from-yellow-500 to-yellow-600' },
        { filterKey: 'approved', name: p('dist_approved'), count: stats.approved, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'paid', name: p('dist_paid'), count: stats.paid, color: 'bg-gradient-to-r from-green-500 to-green-600' },
      ],
    },
    by_salesperson: {
      title: p('dist_by_salesperson_title'),
      data: [
        { filterKey: 'active_salespeople', name: p('dist_active_salespeople'), count: new Set(commissions.map(c => c.salesperson_name)).size, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'avg_commissions', name: p('dist_avg_commissions'), count: commissions.length > 0 ? formatBrandMoneyCompact(stats.totalCommissionAmount / commissions.length) : formatBrandMoneyCompact(0), color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
        { filterKey: 'top_performer', name: p('dist_top_performer'), count: p('top_label'), color: 'bg-gradient-to-r from-green-500 to-green-600' },
      ],
    },
    by_status: {
      title: p('dist_by_status_title'),
      data: [
        { filterKey: 'to_process', name: p('dist_to_process'), count: stats.pending + stats.approved, color: 'bg-gradient-to-r from-yellow-500 to-yellow-600' },
        { filterKey: 'processed', name: p('dist_processed'), count: stats.paid, color: 'bg-gradient-to-r from-green-500 to-green-600' },
      ],
    },
  }), [p, stats, commissions]);

  const distributionInfo = commissionDistribution[mainViewMode] || commissionDistribution.all_commissions;

  const commissionStatusFilterOptions = useMemo(() => ([
    { value: 'pending', label: p('filter_pending') },
    { value: 'approved', label: p('filter_approved') },
    { value: 'paid', label: p('filter_paid') },
    { value: 'cancelled', label: p('filter_cancelled') },
  ]), [p]);

  const columnFilterRules = useMemo(() => ([
    textColumnRule('commission_reference'),
    textColumnRule('salesperson_name'),
    textColumnRule('sale_reference'),
    textColumnRule('commission_rate', (r) => String(r.commission_rate ?? '')),
    textColumnRule('commission_amount', (r) => String(r.commission_amount ?? '')),
    textColumnRule('commission_date', (r) => (r.commission_date ? new Date(r.commission_date).toLocaleDateString(dateLocale) : '')),
    exactColumnRule('status', (r) => r.status ?? ''),
  ]), [dateLocale]);

  const baseFilteredCommissions = useMemo(() => commissions.filter(c => {
    if (!matchesVentesListDateRange(c.commission_date, dateRange)) return false;
    
    if (quickFilter !== 'all' && c.status !== quickFilter) return false;

    if (searchTerm && !c.commission_reference?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !c.salesperson_name?.toLowerCase().includes(searchTerm.toLowerCase()) && 
        !c.sale_reference?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    
    if (activeDistributionFilter) {
      switch(mainViewMode) {
        case 'all_commissions':
          if (activeDistributionFilter === 'pending' && c.status !== 'pending') return false;
          if (activeDistributionFilter === 'approved' && c.status !== 'approved') return false;
          if (activeDistributionFilter === 'paid' && c.status !== 'paid') return false;
          break;
        case 'by_status':
          if (activeDistributionFilter === 'to_process' && !['pending', 'approved'].includes(c.status)) return false;
          if (activeDistributionFilter === 'processed' && c.status !== 'paid') return false;
          break;
      }
    }
    
    return true;
  }), [commissions, quickFilter, searchTerm, activeDistributionFilter, mainViewMode, dateRange]);

  const filteredCommissions = useMemo(
    () => applyColumnFilters(baseFilteredCommissions, filters, columnFilterRules),
    [baseFilteredCommissions, filters, columnFilterRules],
  );

  const filteredCommissionIds = useMemo(() => filteredCommissions.map((r) => r.id), [filteredCommissions]);
  const displayedCommissions = useMemo(
    () => sliceVentesListItems(filteredCommissions, ventesConfig.itemsPerPage),
    [filteredCommissions, ventesConfig.itemsPerPage],
  );
  const { allRowsSelected, someRowsSelected } = useMemo(
    () => computeVentesRowSelection(filteredCommissionIds, selectedRecords),
    [filteredCommissionIds, selectedRecords],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const commissionReference = resolveVentesEntityReference(
        settings,
        'commission',
        commissions,
        formData.commission_reference,
      );
      const submitData = { 
        commission_reference: commissionReference,
        salesperson_name: formData.salesperson_name,
        employee_id: formData.employee_id || '',
        sale_reference: formData.sale_reference || '',
        sale_amount: parseFloat(formData.sale_amount) || 0,
        commission_rate: parseFloat(formData.commission_rate) || 0,
        commission_amount: parseFloat(formData.commission_amount) || 0,
        commission_date: formData.commission_date,
        payment_date: formData.payment_date || null,
        status: formData.status,
        payment_method: formData.payment_method || '',
        notes: formData.notes || ''
      };
      
      if (editingRecord) {
        await localApiAdapter.put(`/ventes/commissions/${editingRecord.id}`, submitData);
        notifyUpdated(showNotify, p('entity_label', { defaultValue: 'عمولة' }));
      } else {
        await localApiAdapter.post('/ventes/commissions', submitData);
        notifyCreated(showNotify, p('entity_label', { defaultValue: 'عمولة' }));
      }
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('alert_save_error', { message: error.message || p('unknown_error') }));
      return;
    } finally {
      resetForm();
      loadCommissions();
    }
  };

  const handleEdit = (record) => {
    setFormTab('basic');
    setEditingRecord(record);
    setFormData({ 
      commission_reference: record.commission_reference || '',
      salesperson_name: record.salesperson_name || '',
      employee_id: record.employee_id || '',
      sale_reference: record.sale_reference || '',
      sale_amount: record.sale_amount?.toString() || '',
      commission_rate: record.commission_rate?.toString() || '',
      commission_amount: record.commission_amount?.toString() || '',
      commission_date: record.commission_date ? record.commission_date.substring(0, 10) : '',
      payment_date: record.payment_date ? record.payment_date.substring(0, 10) : '',
      status: record.status || 'pending',
      payment_method: record.payment_method || '',
      notes: record.notes || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await localApiAdapter.delete(`/ventes/commissions/${id}`);
        notifyDeleted(showNotify, p('entity_label', { defaultValue: 'عمولة' }));
        loadCommissions();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('alert_delete_error', { defaultValue: 'تعذّر حذف العمولة' }));
      }
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('delete_bulk_confirm', { count: selectedRecords.length }))) {
      try {
        const deleteCount = selectedRecords.length;
        await Promise.all(selectedRecords.map(id => localApiAdapter.delete(`/ventes/commissions/${id}`)));
        notifyBulkDeleted(showNotify, deleteCount, p('entity_label_plural', { defaultValue: 'عمولة' }));
        setSelectedRecords([]);
        loadCommissions();
      } catch (error) {
        console.error('Error deleting records:', error);
        notifyError(showNotify, p('alert_bulk_delete_error', { defaultValue: 'تعذّر حذف العمولات المحددة' }));
      }
    }
  };

  const handleExport = () => {
    const headers = [
      { key: 'salesperson_name', label: p('col_salesperson') },
      { key: 'sale_reference', label: p('col_sale') },
      { key: 'sale_amount', label: p('col_sale_amount', { defaultValue: p('col_sale') }) },
      { key: 'commission_rate', label: p('col_rate') },
      { key: 'commission_amount', label: p('col_commission_amount', { defaultValue: p('col_rate') }) },
      { key: 'commission_date', label: p('col_commission_date', { defaultValue: p('col_date') }) },
      { key: 'status', label: p('col_status') },
    ];
    const fileName = `commissions_${new Date().toISOString().split('T')[0]}`;
    try {
      exportToCSV(filteredCommissions, headers, `${fileName}.csv`);
    } catch (error) {
      console.error('Export error:', error);
    }
  };

  const handleExportPDF = async () => {
    try {
      setIsGeneratingPDF(true);
      await generateReportPDF(p('report_pdf_title', { defaultValue: p('title') }), {});
    } catch (error) {
      console.error('PDF export error:', error);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setIsGeneratingExcel(true);
      await generateReportExcel('commissions', p('report_pdf_title', { defaultValue: p('title') }), {});
    } catch (error) {
      console.error('Excel export error:', error);
    } finally {
      setIsGeneratingExcel(false);
    }
  };

  const handleImport = () => {
    const headers = [
      { key: 'salesperson_name', label: p('col_salesperson') },
      { key: 'sale_reference', label: p('col_sale') },
      { key: 'commission_rate', label: p('col_rate') },
      { key: 'commission_amount', label: p('col_commission_amount', { defaultValue: p('col_rate') }) },
      { key: 'commission_date', label: p('col_commission_date', { defaultValue: p('col_date') }) },
    ];
    const schema = {
      requiredFields: ['salesperson_name', 'commission_date'],
      validators: {
        commission_rate: (value) => !value || !isNaN(parseFloat(value)),
        commission_amount: (value) => !value || !isNaN(parseFloat(value)),
      },
    };
    triggerFileInput(async (file) => {
      try {
        const result = await importFromFile(file, headers, schema);
        if (result.success) {
          let successCount = 0;
          for (const record of result.data) {
            try {
              await localApiAdapter.post('/ventes/commissions', record);
              successCount++;
            } catch (err) {
              console.error('Import record error:', err);
            }
          }
          await loadCommissions();
          alert(p('import_success', { total: result.totalCount, imported: successCount, defaultValue: `${successCount}/${result.totalCount} imported` }));
        }
      } catch (error) {
        console.error('Import error:', error);
      }
    }, '.csv,.xlsx,.xls');
  };

  const openCreateCommissionModal = () => {
    if (!ventesConfig.enableCommissions) return;
    setEditingRecord(null);
    setFormTab('basic');
    setFormData({
      commission_reference: ventesConfig.autoGenerateCommissionReference !== false ? generateCommissionRef() : '',
      salesperson_name: '',
      employee_id: '',
      sale_reference: '',
      sale_amount: '',
      commission_rate: String(ventesConfig.defaultCommissionRate ?? ''),
      commission_amount: '',
      commission_date: '',
      payment_date: '',
      status: ventesConfig.requireApprovalBeforePayment ? 'pending' : 'approved',
      payment_method: '',
      notes: '',
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({ 
      commission_reference: '',
      salesperson_name: '', 
      employee_id: '',
      sale_reference: '',
      sale_amount: '',
      commission_rate: '',
      commission_amount: '',
      commission_date: '',
      payment_date: '',
      status: 'pending',
      payment_method: '',
      notes: ''
    });
    setEditingRecord(null);
    setFormTab('basic');
    setShowModal(false);
  };

  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(filteredCommissionIds);
    }
  };
  
  const handleSelectRecord = (id) => { 
    setSelectedRecords(selectedRecords.includes(id) ? selectedRecords.filter(rId => rId !== id) : [...selectedRecords, id]); 
  };

  // Auto-calculate commission amount
  useEffect(() => {
    if (formData.sale_amount && formData.commission_rate) {
      const amount = parseFloat(formData.sale_amount) * (parseFloat(formData.commission_rate) / 100);
      setFormData(prev => ({ ...prev, commission_amount: amount.toFixed(2) }));
    }
  }, [formData.sale_amount, formData.commission_rate]);

  const statusOptions = useMemo(() => ([
    { value: 'pending', label: p('status_pending'), icon: Clock, style: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
    { value: 'approved', label: p('status_approved'), icon: CheckCircle, style: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    { value: 'paid', label: p('status_paid'), icon: Check, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'cancelled', label: p('status_cancelled'), icon: X, style: 'bg-red-500/20 text-red-400 border-red-500/30' },
  ]), [p]);

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.put(`/ventes/commissions/${recordId}`, { status: newStatus });
      await loadCommissions();
    } catch (error) {
      console.error('❌ Error updating status:', error);
    }
  };

  const listStats = useMemo(
    () => mapVentesStatCards([
      { key: 'total', label: p('stat_total'), value: stats.total, icon: Receipt, variant: 'copper' },
      { key: 'pending', label: p('stat_pending'), value: stats.pending, icon: Clock, variant: 'forest' },
      { key: 'approved', label: p('stat_approved'), value: stats.approved, icon: CheckCircle, variant: 'copper' },
      { key: 'paid', label: p('stat_paid'), value: stats.paid, icon: Check, variant: 'forest' },
    ]),
    [stats, p],
  );

  const advancedOptionsContent = (
    <div className="space-y-6 pt-6">
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
        <div className="flex space-x-1 overflow-x-auto table-scroll">
          {mainTabs.map((tab) => (
            <button key={tab.id} type="button" onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
              <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
            </button>
          ))}
        </div>
      </div>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
          {activeDistributionFilter && (
            <button type="button" onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
              <X size={14} />{p('reset_filter')}
            </button>
          )}
        </div>
        <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '3')}>{distributionInfo.data.map((item) => (
            <button key={item.filterKey || item.name} type="button" onClick={() => setActiveDistributionFilter(activeDistributionFilter === (item.filterKey || item.name) ? null : (item.filterKey || item.name))} className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === (item.filterKey || item.name) ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`} title={p('filter_by_title', { name: item.name })}>
              <div className="flex justify-between items-center mb-2">
                <h4 className={`font-medium ${activeDistributionFilter === (item.filterKey || item.name) ? 'text-white' : 'text-white/80'}`}>{item.name}</h4>
                <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
              </div>
            </button>
          ))}
        </div>
      </motion.div>
    </div>
  );

  return (
    <>
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
      <VentesConfigNotice
        message={!ventesConfig.enableCommissions ? p('notice_commissions_disabled', { defaultValue: 'العمولات معطّلة — فعّلها من إعدادات المبيعات ← العمولات.' }) : null}
      />
      <VentesListPageChrome
        t={t}
        p={p}
        qawaem={qawaem}
        isDashboardLightMode={isDashboardLightMode}
        title={p('title')}
        subtitle={p('subtitle')}
        icon={DollarSign}
        listStats={listStats}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        filterActions={buildVentesBulkDeleteFilterAction({
          selectedCount: selectedRecords.length,
          onDelete: handleBulkDelete,
          label: p('bulk_delete_confirm', { count: selectedRecords.length }),
        })}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={p('search_placeholder', { defaultValue: t('common.search') })}
        onCreate={ventesConfig.enableCommissions ? openCreateCommissionModal : undefined}
        createLabel={p('new_commission')}
        importExportState={{ showImportDropdown, setShowImportDropdown, showExportDropdown, setShowExportDropdown, importDropdownRef, exportDropdownRef }}
        importExportHandlers={{
          onImport: handleImport,
          onImportCSV: handleImport,
          onImportExcel: handleImport,
          onExportCSV: handleExport,
          onExportExcel: handleExportExcel,
          onExportPDF: handleExportPDF,
        }}
        isGeneratingPDF={isGeneratingPDF}
        showAdvancedOptions={showAdvancedOptions}
        onToggleAdvancedOptions={() => setShowAdvancedOptions(!showAdvancedOptions)}
        advancedOptionsLabel={t('common.advanced_options')}
        advancedOptionsContent={advancedOptionsContent}
      >
        {viewMode === 'table' && (
          <div className={facturationTableScrollClass(qawaem)}>
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        size="lg"
                        checked={allRowsSelected}
                        indeterminate={someRowsSelected}
                        onChange={handleSelectAll}
                        title={allRowsSelected ? p('deselect_all', { defaultValue: t('common.deselect_all') }) : p('select_all', { defaultValue: t('common.select_all') })}
                      />
                    </div>
                  </th>
                  
                  <TableColumnHeaderFilter columnKey="commission_reference" label={p('col_code')} value={filters.commission_reference || ''} onChange={(v) => setFilter('commission_reference', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  <TableColumnHeaderFilter columnKey="salesperson_name" label={p('col_salesperson')} value={filters.salesperson_name || ''} onChange={(v) => setFilter('salesperson_name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  {showCol('sale_reference') ? (
                    <TableColumnHeaderFilter columnKey="sale_reference" label={p('col_sale_ref')} value={filters.sale_reference || ''} onChange={(v) => setFilter('sale_reference', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  ) : null}
                  {showCol('commission_rate') ? (
                    <TableColumnHeaderFilter columnKey="commission_rate" label={p('col_rate')} value={filters.commission_rate || ''} onChange={(v) => setFilter('commission_rate', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  ) : null}
                  {showCol('commission_amount') ? (
                    <TableColumnHeaderFilter columnKey="commission_amount" label={p('col_commission')} value={filters.commission_amount || ''} onChange={(v) => setFilter('commission_amount', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  ) : null}
                  {showCol('commission_date') ? (
                    <TableColumnHeaderFilter columnKey="commission_date" label={p('col_date')} value={filters.commission_date || ''} onChange={(v) => setFilter('commission_date', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  ) : null}
                  {showCol('status') ? (
                    <TableColumnHeaderFilter columnKey="status" label={p('col_status')} kind="select" options={commissionStatusFilterOptions} value={filters.status || ''} onChange={(v) => setFilter('status', v)} align="center" editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  ) : null}
                  <QawaemTableHeaderActionsCell
                    label={t('common.actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                    thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {displayedCommissions.length > 0 ? (
                  displayedCommissions.map((record, index) => (
                    <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                      <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                        <div className="flex justify-center">
                          <QawaemTableCheckbox
                            checked={selectedRecords.includes(record.id)}
                            onChange={() => handleSelectRecord(record.id)}
                          />
                        </div>
                      </td>
                      
                      <td className="px-6 py-4">
                        <span className="text-white font-semibold font-mono">{record.commission_reference || p('na')}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold flex items-center gap-2">
                            <User size={14} className="text-blue-400" />
                            {record.salesperson_name}
                          </span>
                          <span className="text-white/50 text-xs mt-0.5">ID: {record.employee_id || p('na')}</span>
                        </div>
                      </td>
                      {showCol('sale_reference') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-white font-semibold">{record.sale_reference || 'N/A'}</span>
                            <span className="text-white/50 text-xs mt-0.5">{formatBrandMoney(record.sale_amount || 0)} · {p('vat_rate_short', { rate: vatRate })}</span>
                          </div>
                        </td>
                      ) : null}
                      {showCol('commission_rate') ? (
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Percent size={14} className="text-purple-400" />
                            <span className="text-white font-semibold">{record.commission_rate || 0}%</span>
                          </div>
                        </td>
                      ) : null}
                      {showCol('commission_amount') ? (
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <DollarSign size={14} className="text-green-400" />
                            <span className="text-white font-semibold">{formatBrandMoney(record.commission_amount || 0)}</span>
                          </div>
                        </td>
                      ) : null}
                      {showCol('commission_date') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <div className="flex items-center gap-1.5 text-white font-semibold">
                              <Clock size={14} className="text-white/50" />
                              {record.commission_date ? new Date(record.commission_date).toLocaleDateString(dateLocale) : '-'}
                            </div>
                            {record.payment_date && (
                              <span className="text-white/50 text-xs mt-0.5">{p('label_payment')}: {new Date(record.payment_date).toLocaleDateString(dateLocale)}</span>
                            )}
                          </div>
                        </td>
                      ) : null}
                      {showCol('status') ? (
                        <td className="px-6 py-4 text-center">
                          <div className="flex justify-center">
                            <QawaemClickableStatusBadge
                              status={record.status}
                              recordId={record.id}
                              options={statusOptions}
                              onChange={handleStatusChange}
                              title={p('click_change_status')}
                            />
                          </div>
                        </td>
                      ) : null}
                      <td className="px-6 py-4">
                        <QawaemErpRowActions
                          onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                          onEdit={() => handleEdit(record)}
                          onDelete={() => handleDelete(record.id)}
                          viewTitle={t('common.view')}
                          editTitle={t('common.edit')}
                          deleteTitle={t('common.delete')}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60"><DollarSign size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty')}</p></td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {displayedCommissions.length > 0 ? (
              displayedCommissions.map((record, index) => (
                <motion.div
                  key={record.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <User size={20} className="text-blue-400" />
                          {record.salesperson_name}
                        </h3>
                        <QawaemClickableStatusBadge
                          status={record.status}
                          recordId={record.id}
                          options={statusOptions}
                          onChange={handleStatusChange}
                          title={p('click_change_status')}
                        />
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-white/20 text-white/80">
                          {formatBrandMoney(record.commission_amount || 0)}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_salesperson')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_employee_id')}</span> {record.employee_id || p('na')}</div>
                            <div><span className="font-medium">{p('label_name')}</span> {record.salesperson_name}</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('label_sale_reference')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_sale_ref')}</span> {record.sale_reference || p('na')}</div>
                            <div className="flex items-center gap-2">
                              <DollarSign size={16} className="text-green-400" />
                              <span className="font-medium">{p('label_sale_amount')}</span> <span className="text-white">{formatBrandMoney(record.sale_amount || 0)}</span>
                            </div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_commission')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div className="flex items-center gap-2">
                              <Percent size={16} className="text-purple-400" />
                              <span className="font-medium">{p('label_rate')}</span> {record.commission_rate || 0}%
                            </div>
                            <div className="flex items-center gap-2">
                              <DollarSign size={16} className="text-green-400" />
                              <span className="font-medium">{p('label_commission')}</span> <span className="text-white">{formatBrandMoney(record.commission_amount || 0)}</span>
                            </div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_payment')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_commission_date')}</span> {record.commission_date ? new Date(record.commission_date).toLocaleDateString(dateLocale) : '-'}</div>
                            <div><span className="font-medium">{p('label_payment')}:</span> {record.payment_date ? new Date(record.payment_date).toLocaleDateString(dateLocale) : p('label_not_paid')}</div>
                            <div><span className="font-medium">{p('label_payment_method')}</span> {record.payment_method || p('na')}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                      onEdit={() => handleEdit(record)}
                      onDelete={() => handleDelete(record.id)}
                      viewTitle={t('common.view')}
                      editTitle={t('common.edit')}
                      deleteTitle={t('common.delete')}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <DollarSign size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty')}</p>
              </div>
            )}
          </div>
        )}
      </VentesListPageChrome>

      {showViewModal && viewingRecord ? (
      <QawaemErpViewModal
        open
        onClose={() => setShowViewModal(false)}
        title={p('view_title')}
        icon={Eye}
        maxWidth="1100px"
        testId="commissions-view-modal"
      >
              <div className="space-y-4">
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <User size={18} className="text-blue-400" />
                    {p('section_salesperson')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('col_code')}</p>
                      <p className="text-white font-medium text-lg font-mono">{viewingRecord.commission_reference || p('na')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_salesperson_name')}</p>
                      <p className="text-white font-medium text-lg">{viewingRecord.salesperson_name}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_employee_id_field')}</p>
                      <p className="text-white font-medium">{viewingRecord.employee_id || p('na')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_status')}</p>
                      <QawaemClickableStatusBadge status={viewingRecord.status} recordId={viewingRecord.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status')} />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <Receipt size={18} className="text-purple-400" />
                      {p('label_sale_reference')}
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_sale_reference')}</p>
                        <p className="text-white font-medium">{viewingRecord.sale_reference || p('na')}</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_sale_amount_field')}</p>
                        <p className="text-white font-medium">{formatBrandMoney(viewingRecord.sale_amount || 0)}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <DollarSign size={18} className="text-green-400" />
                      {p('section_commission')}
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_commission_rate')}</p>
                        <p className="text-white font-medium">{viewingRecord.commission_rate || 0}%</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_commission_amount')}</p>
                        <p className="text-white font-medium">{formatBrandMoney(viewingRecord.commission_amount || 0)}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <Calendar size={18} className="text-blue-400" />
                    {p('section_payment')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_commission_date_field')}</p>
                      <p className="text-white font-medium">{viewingRecord.commission_date ? new Date(viewingRecord.commission_date).toLocaleDateString(dateLocale) : '-'}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_payment_date_field')}</p>
                      <p className="text-white font-medium">{viewingRecord.payment_date ? new Date(viewingRecord.payment_date).toLocaleDateString(dateLocale) : p('label_not_paid')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_payment_method_field')}</p>
                      <p className="text-white font-medium">{viewingRecord.payment_method || p('na')}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <FileText size={18} className="text-purple-400" />
                    {p('section_notes')}
                  </h3>
                  <p className="text-white/80">{viewingRecord.notes || p('no_notes')}</p>
                </div>
              </div>
      </QawaemErpViewModal>
      ) : null}

      <QawaemErpCrudModal
        open={showModal}
        onClose={resetForm}
        title={editingRecord ? p('modal_edit') : p('modal_create')}
        icon={DollarSign}
        showSaveButton
        formId="commission-form"
        saveLabel={editingRecord ? p('update') : p('create')}
        testId="commissions-crud-modal"
        tabs={commissionFormTabs}
        activeTab={formTab}
        onTabChange={setFormTab}
        tabsAriaLabel={editingRecord ? p('modal_edit') : p('modal_create')}
      >
        <form id="commission-form" onSubmit={handleSubmit}>
          {formTab === 'basic' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_commission_reference')} <span className="qawaem-required-mark">*</span></label>
                <input type="text" required value={formData.commission_reference || ''} onChange={(e) => setFormData({ ...formData, commission_reference: e.target.value })} className={VENTES_REF_INPUT_CLASS} dir="ltr" />
              </div>
              <div>
                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_salesperson_name')} <span className="qawaem-required-mark">*</span></label>
                <input type="text" required value={formData.salesperson_name} onChange={(e) => setFormData({ ...formData, salesperson_name: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('ph_salesperson')} />
              </div>
              <div>
                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_employee_id')}</label>
                <input type="text" value={formData.employee_id} onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('ph_employee_id')} />
              </div>
              <div>
                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_status')}</label>
                <CustomSelect value={formData.status} onChange={(value) => setFormData({ ...formData, status: value })} options={statusOptions.map(s => ({ value: s.value, label: s.label, icon: <s.icon size={16} /> }))} placeholder={p('ph_status')} />
              </div>
            </div>
          )}
          {formTab === 'commission' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_sale_reference')}</label>
                <input type="text" value={formData.sale_reference} onChange={(e) => setFormData({ ...formData, sale_reference: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('ph_sale_ref')} />
              </div>
              <div>
                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_sale_amount')}</label>
                <CustomNumberInput value={formData.sale_amount} onChange={(value) => setFormData({ ...formData, sale_amount: value })} placeholder={p('placeholder_zero')} step={1000} min={0} />
              </div>
              <div>
                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_commission_rate')}</label>
                <CustomNumberInput value={formData.commission_rate} onChange={(value) => setFormData({ ...formData, commission_rate: value })} placeholder={p('placeholder_zero')} step={1} min={0} max={100} />
              </div>
              <div>
                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_commission_amount')}</label>
                <CustomNumberInput value={formData.commission_amount} onChange={(value) => setFormData({ ...formData, commission_amount: value })} placeholder={p('placeholder_zero')} step={100} min={0} />
              </div>
              <div>
                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_commission_date')}</label>
                <CustomDatePicker value={formData.commission_date} onChange={(date) => setFormData({ ...formData, commission_date: date })} placeholder={p('ph_date')} />
              </div>
              <div>
                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_payment_date')}</label>
                <CustomDatePicker value={formData.payment_date} onChange={(date) => setFormData({ ...formData, payment_date: date })} placeholder={p('ph_date')} minDate={formData.commission_date} />
              </div>
              <div>
                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_payment_method')}</label>
                <CustomSelect
                  value={formData.payment_method}
                  onChange={(value) => setFormData({ ...formData, payment_method: value })}
                  options={[
                    { value: 'cash', label: p('method_cash') },
                    { value: 'bank_transfer', label: p('method_bank_transfer') },
                    { value: 'check', label: p('method_check') },
                    { value: 'card', label: p('method_card') },
                  ]}
                  placeholder={p('ph_payment_method')}
                />
              </div>
              <div className="md:col-span-3">
                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('section_notes_form')}</label>
                <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={4} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none" placeholder={p('ph_notes')} />
              </div>
            </div>
          )}
        </form>
      </QawaemErpCrudModal>
    </>
  );
};

export default CommissionsPage;

